import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from 'sonner';
import {
  Building,
  ExternalLink,
  FileText,
  Eye,
  CalendarIcon,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { formatTextContent } from '@/utils/text';
import { Link } from 'react-router-dom';

interface Business {
  id: string;
  business_name: string;
  company_name: string;
  tagline: string;
  description: string;
  logo_url: string | null;
  pitch_deck_url: string | null;
  development_stage: string;
  industry_type: string;
  website: string | null;
  contact_email: string | null;
  problem_statement: string | null;
  solution_description: string | null;
  market_size: string | null;
  funding_goal: number | null;
  investment_highlights: string[] | null;
  competitive_edge: string[] | null;
  target_segment: string | null;
  growth_drivers: string | null;
  team_members: {
    name: string;
    role: string;
    bio: string;
    linkedin?: string;
    image_url?: string;
  }[] | null;
  business_model: string | null;
  traction: string | null;
  financial_projections: string | null;
  created_at: string;
  updated_at: string;
  status: 'draft' | 'active';
}

export const InvestorBusinessList = () => {
  const { user } = useAuth();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) {
        console.log('No authenticated user found');
        setBusinesses([]);
        return;
      }

      const { data, error } = await supabase
        .from('businesses')
        .select(`
          id,
          business_name,
          company_name,
          tagline,
          description,
          logo_url,
          pitch_deck_url,
          development_stage,
          industry_type,
          website,
          contact_email,
          problem_statement,
          solution_description,
          market_size,
          funding_goal,
          investment_highlights,
          competitive_edge,
          target_segment,
          growth_drivers,
          team_members,
          business_model,
          traction,
          financial_projections,
          created_at,
          updated_at,
          status
        `)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching businesses:', error);
        setBusinesses([]);
        toast.error('Failed to load investment opportunities');
        return;
      }

      if (!data) {
        console.log('No businesses found');
        setBusinesses([]);
        return;
      }

      setBusinesses(data);
      
    } catch (error) {
      console.error('Error in fetchBusinesses:', error);
      setBusinesses([]);
      toast.error('Failed to load investment opportunities');
    } finally {
      setLoading(false);
    }
  };

  const openBusinessDetails = (business: Business) => {
    setSelectedBusiness(business);
    setIsDetailsDialogOpen(true);
    
    // Update viewed_profiles metric for the investor
    if (user) {
      updateViewedProfilesMetric(user.id);
    }
  };

  const updateViewedProfilesMetric = async (userId: string) => {
    try {
      // Get current metrics
      const { data, error } = await supabase
        .from('investor_metrics')
        .select('viewed_profiles')
        .eq('user_id', userId)
        .single();
      
      if (error) {
        console.error('Error fetching investor metrics:', error);
        return;
      }
      
      // Increment viewed_profiles
      const currentViews = data?.viewed_profiles || 0;
      
      const { error: updateError } = await supabase
        .from('investor_metrics')
        .update({ viewed_profiles: currentViews + 1 })
        .eq('user_id', userId);
      
      if (updateError) {
        console.error('Error updating viewed profiles metric:', updateError);
      }
    } catch (error) {
      console.error('Error updating metrics:', error);
    }
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null) return 'Not specified';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Investment Opportunities</h2>
      </div>
      
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="bg-card border border-border">
              <CardHeader className="pb-2">
                <div className="w-full h-4 bg-secondary/40 rounded animate-pulse mb-2"></div>
                <div className="w-2/3 h-3 bg-secondary/40 rounded animate-pulse"></div>
              </CardHeader>
              <CardContent>
                <div className="w-full h-20 bg-secondary/30 rounded animate-pulse"></div>
              </CardContent>
              <CardFooter>
                <div className="w-full h-8 bg-secondary/40 rounded animate-pulse"></div>
              </CardFooter>
            </Card>
          ))}
        </div>
      ) : businesses.length === 0 ? (
        <Card className="bg-card border border-border">
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground">No investment opportunities available at this time.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {businesses.map((business) => (
            <Card key={business.id} className="bg-card border border-border flex flex-col h-full">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3 mb-2">
                  {business.logo_url ? (
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={business.logo_url} alt={business.business_name} />
                      <AvatarFallback>
                        <Building className="h-6 w-6" />
                      </AvatarFallback>
                    </Avatar>
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Building className="h-6 w-6 text-primary" />
                    </div>
                  )}
                  <CardTitle className="text-lg">{business.business_name}</CardTitle>
                </div>
                <CardDescription className="line-clamp-1">{business.tagline}</CardDescription>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge variant="outline" className="bg-primary/5">
                    {business.industry_type}
                  </Badge>
                  <Badge variant="outline" className="bg-secondary/10">
                    {business.development_stage}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="flex-grow">
                <p className="text-sm text-card-foreground line-clamp-3 mb-4">
                  {business.description}
                </p>
                {business.funding_goal && (
                  <div className="mt-2">
                    <p className="text-sm font-medium">Funding Goal</p>
                    <p className="text-primary font-semibold">
                      {formatCurrency(business.funding_goal)}
                    </p>
                  </div>
                )}
              </CardContent>
              <CardFooter className="flex justify-between pt-2 border-t border-border">
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => openBusinessDetails(business)}
                    className="flex items-center gap-2"
                  >
                    <Eye className="h-4 w-4" />
                    View Details
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                    asChild
                  >
                    <Link to={`/schedule-call?business=${business.id}`}>
                      <CalendarIcon className="h-4 w-4" />
                      Schedule Meeting
                    </Link>
                  </Button>
                </div>
                {business.pitch_deck_url && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex items-center gap-2"
                    asChild
                  >
                    <a href={business.pitch_deck_url} target="_blank" rel="noopener noreferrer">
                      <FileText className="h-4 w-4" />
                      Pitch Deck
                    </a>
                  </Button>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>
      )}
      
      {/* Business Details Dialog */}
      <Dialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
        {selectedBusiness && (
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center gap-3">
                {selectedBusiness.logo_url ? (
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={selectedBusiness.logo_url} alt={selectedBusiness.business_name} />
                    <AvatarFallback>
                      <Building className="h-8 w-8" />
                    </AvatarFallback>
                  </Avatar>
                ) : (
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Building className="h-8 w-8 text-primary" />
                  </div>
                )}
                <DialogTitle className="text-xl">{selectedBusiness.business_name}</DialogTitle>
              </div>
              <DialogDescription className="mt-2">{selectedBusiness.tagline}</DialogDescription>
              <div className="flex flex-wrap gap-2 mt-2">
                <Badge variant="outline" className="bg-primary/5">
                  {selectedBusiness.industry_type}
                </Badge>
                <Badge variant="outline" className="bg-secondary/10">
                  {selectedBusiness.development_stage}
                </Badge>
                {selectedBusiness.website && (
                  <a 
                    href={selectedBusiness.website.startsWith('http') ? selectedBusiness.website : `https://${selectedBusiness.website}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Website
                  </a>
                )}
              </div>
            </DialogHeader>
            
            <div className="mt-6 space-y-8">
              {/* Overview Section */}
              <div>
                <h3 className="text-lg font-semibold mb-2 border-b pb-1">Business Overview</h3>
                <p className="text-card-foreground whitespace-pre-wrap">{formatTextContent(selectedBusiness.description)}</p>
              </div>
              
              {/* Problem & Solution Section */}
              {(selectedBusiness.problem_statement || selectedBusiness.solution_description) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {selectedBusiness.problem_statement && (
                    <div>
                      <h3 className="text-lg font-semibold mb-2 border-b pb-1">Problem</h3>
                      <p className="text-card-foreground whitespace-pre-wrap">{formatTextContent(selectedBusiness.problem_statement)}</p>
                    </div>
                  )}
                  {selectedBusiness.solution_description && (
                    <div>
                      <h3 className="text-lg font-semibold mb-2 border-b pb-1">Solution</h3>
                      <p className="text-card-foreground whitespace-pre-wrap">{formatTextContent(selectedBusiness.solution_description)}</p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Investment Highlights */}
              {selectedBusiness.investment_highlights && selectedBusiness.investment_highlights.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-2 border-b pb-1">Investment Highlights</h3>
                  <ul className="list-disc pl-5 space-y-1">
                    {selectedBusiness.investment_highlights.map((highlight, index) => (
                      <li key={index} className="text-card-foreground">{highlight}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Competitive Edge */}
              {selectedBusiness.competitive_edge && selectedBusiness.competitive_edge.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-2 border-b pb-1">Competitive Edge</h3>
                  <ul className="list-disc pl-5 space-y-1">
                    {selectedBusiness.competitive_edge.map((edge, index) => (
                      <li key={index} className="text-card-foreground">{edge}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Team Members */}
              {selectedBusiness.team_members && Array.isArray(selectedBusiness.team_members) && selectedBusiness.team_members.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-2 border-b pb-1">Team</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedBusiness.team_members.map((member, index) => (
                      <div key={index} className="p-4 bg-secondary/5 rounded-lg">
                        <div className="flex items-center gap-3 mb-2">
                          {member.image_url ? (
                            <Avatar className="h-10 w-10">
                              <AvatarImage src={member.image_url} alt={member.name} />
                              <AvatarFallback>{member.name.charAt(0)}</AvatarFallback>
                            </Avatar>
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-primary font-semibold">{member.name.charAt(0)}</span>
                            </div>
                          )}
                          <div>
                            <h4 className="font-medium">{member.name}</h4>
                            <p className="text-sm text-muted-foreground">{member.role}</p>
                          </div>
                        </div>
                        <p className="text-sm">{member.bio}</p>
                        {member.linkedin && (
                          <a 
                            href={member.linkedin.startsWith('http') ? member.linkedin : `https://${member.linkedin}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                          >
                            <ExternalLink className="h-3 w-3" />
                            LinkedIn
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Funding Information */}
              {(selectedBusiness.funding_goal || selectedBusiness.market_size || selectedBusiness.growth_drivers) && (
                <div>
                  <h3 className="text-lg font-semibold mb-2 border-b pb-1">Funding Information</h3>
                  <div className="p-4 bg-secondary/5 rounded-lg">
                    <div className="flex flex-col md:flex-row md:justify-between">
                      <div>
                        <p className="text-sm font-medium">Funding Goal</p>
                        <p className="text-xl font-bold text-primary">{formatCurrency(selectedBusiness.funding_goal)}</p>
                      </div>
                      {selectedBusiness.market_size && (
                        <div className="mt-4 md:mt-0">
                          <p className="text-sm font-medium">Market Size</p>
                          <p className="text-lg font-semibold">{selectedBusiness.market_size}</p>
                        </div>
                      )}
                    </div>
                    {selectedBusiness.growth_drivers && (
                      <div className="mt-4">
                        <p className="text-sm font-medium">Growth Drivers</p>
                        <p className="text-card-foreground">{selectedBusiness.growth_drivers}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Business Model */}
              {selectedBusiness.business_model && (
                <div>
                  <h3 className="text-lg font-semibold mb-2 border-b pb-1">Business Model</h3>
                  <div className="p-4 bg-secondary/5 rounded-lg">
                    <p className="text-card-foreground">{selectedBusiness.business_model}</p>
                  </div>
                </div>
              )}

              {/* Traction */}
              {selectedBusiness.traction && (
                <div>
                  <h3 className="text-lg font-semibold mb-2 border-b pb-1">Traction</h3>
                  <div className="p-4 bg-secondary/5 rounded-lg">
                    <p className="text-card-foreground">{selectedBusiness.traction}</p>
                  </div>
                </div>
              )}

              {/* Financial Projections */}
              {selectedBusiness.financial_projections && (
                <div>
                  <h3 className="text-lg font-semibold mb-2 border-b pb-1">Financial Projections</h3>
                  <div className="p-4 bg-secondary/5 rounded-lg">
                    <p className="text-card-foreground">{selectedBusiness.financial_projections}</p>
                  </div>
                </div>
              )}
              
              {/* Contact Information */}
              {selectedBusiness.contact_email && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold mb-2 border-b pb-1">Contact Information</h3>
                  <p className="text-card-foreground">
                    For more information about this investment opportunity, please contact:
                    <a 
                      href={`mailto:${selectedBusiness.contact_email}`} 
                      className="ml-2 text-blue-600 hover:underline"
                    >
                      {selectedBusiness.contact_email}
                    </a>
                  </p>
                </div>
              )}
              
              {/* Pitch Deck */}
              {selectedBusiness.pitch_deck_url && (
                <div className="mt-6 flex justify-center">
                  <Button 
                    variant="default" 
                    size="lg"
                    asChild
                    className="w-full md:w-auto"
                  >
                    <a 
                      href={selectedBusiness.pitch_deck_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2"
                    >
                      <FileText className="h-5 w-5" />
                      View Pitch Deck
                    </a>
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
};
